import React, { forwardRef, useCallback, useState } from "react";
import {
  getCountryCallingCode,
} from "react-phone-number-input";
import type { Country, Value } from "react-phone-number-input";
import PhoneInput from "./SearchablePhoneInput";

/**
 * A custom text input component that intercepts paste events to strip
 * duplicate country calling codes. This solves the issue where pasting
 * a full international number (e.g. "+2347083448550") into a field
 * that already displays a non-editable country code prefix fails silently.
 */
const PasteAwareInput = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { countryCallingCode?: string }
>(({ countryCallingCode, ...rest }, ref) => {
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (!countryCallingCode) return; // Let default behavior handle it

      const pastedText = e.clipboardData.getData("text/plain").trim();

      // Check if the pasted text starts with "+" followed by the country code
      const prefixWithPlus = `+${countryCallingCode}`;

      let nationalNumber: string | null = null;

      if (pastedText.startsWith(prefixWithPlus)) {
        nationalNumber = pastedText.slice(prefixWithPlus.length);
      } else if (
        !pastedText.startsWith("+") &&
        pastedText.startsWith(countryCallingCode)
      ) {
        nationalNumber = pastedText.slice(countryCallingCode.length);
      }

      if (nationalNumber !== null) {
        e.preventDefault();

        // Clean up the national number (remove spaces, dashes, etc.)
        const cleaned = nationalNumber.replace(/[\s\-()]/g, "");

        // Use the native input setter to trigger React's onChange
        const input = e.currentTarget;
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          "value"
        )?.set;

        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(input, cleaned);
          input.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
      // If the pasted text doesn't start with the country code,
      // let the default paste behavior handle it normally.
    },
    [countryCallingCode]
  );

  return <input ref={ref} {...rest} onPaste={handlePaste} />;
});

PasteAwareInput.displayName = "PasteAwareInput";

/**
 * BasePhoneInput - A wrapper around react-phone-number-input that handles
 * pasting international numbers gracefully.
 *
 * It passes a custom inputComponent that intercepts paste events and strips
 * duplicate country calling codes, while preserving all other PhoneInput behavior.
 */
interface BasePhoneInputProps {
  value?: Value | string;
  onChange: (value?: Value) => void;
  onCountryChange?: (country?: Country) => void;
  defaultCountry?: Country;
  international?: boolean;
  countryCallingCodeEditable?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
  onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
  numberInputProps?: Record<string, unknown>;
  disabled?: boolean;
  readOnly?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const BasePhoneInput: React.FC<BasePhoneInputProps> = ({
  onCountryChange,
  defaultCountry,
  ...restProps
}) => {
  const [currentCountry, setCurrentCountry] = useState<Country | undefined>(
    defaultCountry
  );

  const callingCode = currentCountry
    ? getCountryCallingCode(currentCountry)
    : undefined;

  const handleCountryChange = useCallback(
    (country?: Country) => {
      setCurrentCountry(country);
      onCountryChange?.(country);
    },
    [onCountryChange]
  );

  return (
    <PhoneInput
      {...restProps}
      defaultCountry={defaultCountry}
      onCountryChange={handleCountryChange}
      inputComponent={PasteAwareInput}
      numberInputProps={{
        ...restProps.numberInputProps,
        countryCallingCode: callingCode,
      }}
    />
  );
};

export default BasePhoneInput;
